import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  CheckCircle,
  User,
  Mail,
  Phone,
  Heart,
  Send,
  ArrowLeft,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramRegistrationPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    program_interest: '',
    goals: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const programs = [
    { id: 'optimization', name: 'Optimization Program' },
    { id: 'weight_loss', name: 'Weight Loss Program' },
    { id: 'hormone_optimization', name: 'Hormone Optimization' },
    { id: 'longevity', name: 'Longevity Program' },
    { id: 'mental_health', name: 'Mental Health Program' },
    { id: 'life_coaching', name: 'Life Coaching' },
    { id: 'virtual_urgent_care', name: 'Virtual Urgent Care' },
    { id: 'other', name: 'Other / Not Sure' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, program_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.program_interest) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/register-program`, formData);
      
      if (response.data.status === 'success') {
        setSubmitted(true);
        toast.success('Registration submitted successfully!');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white pt-20 pb-12" data-testid="registration-success">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white/95 backdrop-blur-sm p-10 rounded-2xl shadow-xl border-2 border-[#1E3A8A] text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-black text-[#1E3A8A] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Registration Received!
            </h1>
            <p className="text-slate-600 text-lg mb-6 font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              Thank you for your interest in our wellness programs. Our team will contact you within 24-48 hours to discuss your goals and get you started.
            </p>
            <div className="bg-[#1E3A8A]/5 p-4 rounded-lg mb-8">
              <p className="text-[#1E3A8A] font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                In the meantime, download the Spruce Health app to connect with us:
              </p>
              <a 
                href="https://spruce.care/integratedwellness" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-3 text-white text-lg font-bold px-6 py-3 rounded-lg transition-all shadow-lg"
                style={{ 
                  background: 'linear-gradient(to right, #1E3A8A, #3B82F6)',
                  fontFamily: 'Merriweather, serif'
                }}
              >
                Download Spruce Health
              </a>
            </div>
            <Link to="/">
              <Button variant="outline" className="border-2 border-[#1E3A8A] text-[#1E3A8A]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20 pb-12" data-testid="program-registration-page">
      {/* Background Logo Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none opacity-30" style={{ zIndex: 0 }}>
        <img 
          alt="" 
          className="w-[80vmin] h-[80vmin] object-contain"
          src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
        />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#3B82F6]/20 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            <Sparkles className="w-4 h-4" />
            Program Registration
          </div>
          <h1 className="text-3xl lg:text-4xl font-black text-[#1E3A8A] mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
            Register for a Wellness Program
          </h1>
          <p className="text-slate-600 text-lg font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            Submit your information and our team will reach out to get you started.
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-[#1E3A8A] font-bold flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
                <User className="w-4 h-4" />
                First Name *
              </Label>
              <Input
                id="first_name"
                name="first_name"
                type="text"
                required
                value={formData.first_name}
                onChange={handleChange}
                placeholder="John"
                className="input-default"
                data-testid="input-first-name"
              />
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="last_name" className="text-[#1E3A8A] font-bold flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
                <User className="w-4 h-4" />
                Last Name *
              </Label>
              <Input
                id="last_name"
                name="last_name"
                type="text"
                required
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Doe"
                className="input-default"
                data-testid="input-last-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1E3A8A] font-bold flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
                <Mail className="w-4 h-4" />
                Email Address *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className="input-default"
                data-testid="input-email"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#1E3A8A] font-bold flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
                <Phone className="w-4 h-4" />
                Phone Number *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="(201) 555-1234"
                className="input-default"
                data-testid="input-phone"
              />
            </div>
          </div>

          {/* Program Interest */}
          <div className="space-y-2 mt-6">
            <Label htmlFor="program_interest" className="text-[#1E3A8A] font-bold flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
              <Heart className="w-4 h-4" />
              Program of Interest *
            </Label>
            <Select onValueChange={handleSelectChange} value={formData.program_interest}>
              <SelectTrigger className="input-default" data-testid="select-program">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.name}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Goals */}
          <div className="space-y-2 mt-6">
            <Label htmlFor="goals" className="text-[#1E3A8A] font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              What are your health goals? (optional)
            </Label>
            <Textarea
              id="goals"
              name="goals"
              value={formData.goals}
              onChange={handleChange}
              placeholder="Tell us about what you'd like to achieve..."
              rows={3}
              className="input-default resize-none"
              data-testid="textarea-goals"
            />
          </div>

          {/* Additional Message */}
          <div className="space-y-2 mt-6">
            <Label htmlFor="message" className="text-[#1E3A8A] font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              Additional Information (optional)
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Any other details you'd like to share..."
              rows={3}
              className="input-default resize-none"
              data-testid="textarea-message"
            />
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-14 text-lg font-bold"
              style={{ 
                background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                fontFamily: 'Merriweather, serif'
              }}
              data-testid="submit-registration-btn"
            >
              {submitting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Registration
                </>
              )}
            </Button>
          </div>

          {/* Privacy Note */}
          <p className="text-center text-slate-500 text-sm mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            By submitting this form, you agree to be contacted by our team. 
            Your information is kept confidential and secure.
          </p>
        </form>

        {/* Back Link */}
        <div className="text-center mt-6">
          <Link to="/programs" className="text-[#1E3A8A] font-bold hover:underline inline-flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif' }}>
            <ArrowLeft className="w-4 h-4" />
            Back to Programs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProgramRegistrationPage;
